import React, { useEffect, useState } from 'react';
import { useLocationOptions } from '../../Admin/shared/useLocationOptions';
import { useAuth } from '../../../context/AuthContext';
import { facilitiesApi, FACILITY_TYPE_OPTIONS, OWNERSHIP_OPTIONS, FUNCTIONAL_STATUS_OPTIONS } from '../../../api/facilitiesApi';
import { Field, Select, INP, INP_LOCKED } from '../../reusable/formRecords/formFieldPrimitives';
import { logRegistryProposal } from '../../../api/registryCentralApi';

function parseError(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
}

// "+ Propose" for facility lookups (DCIC-06 biogas host / biogas link,
// DCIC-11 target facility) — registry-first: when the facility isn't in the
// registry yet, it is proposed here as a Draft DCIC-06 record that then goes
// through the normal validation pipeline. Same lightweight direct-create
// pattern as ProposeActorModal.
export default function ProposeFacilityModal({ isOpen, onClose, onCreated, allowedTypes = null }) {
  const { user } = useAuth();
  const roleId = user?.roleId ?? '';
  const stateLocked = roleId === 'R04' || roleId === 'R05';
  const lgaLocked = roleId === 'R05';
  const blank = () => ({
    name: '',
    facility_type: allowedTypes?.length === 1 ? allowedTypes[0] : '',
    ownership: '',
    functional_status: 'active',
    state: stateLocked ? (user?.assignedState ?? '') : '',
    lga: lgaLocked ? (user?.assignedLga ?? '') : '',
  });
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { stateOptions, lgaOptions, loadingLgas } = useLocationOptions(form.state);

  useEffect(() => {
    if (!isOpen) return;
    setForm(blank());
    setError('');
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const typeOptions = allowedTypes?.length
    ? FACILITY_TYPE_OPTIONS.filter((o) => allowedTypes.includes(o.value))
    : FACILITY_TYPE_OPTIONS;
  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => (key === 'state' ? { ...prev, state: value, lga: '' } : { ...prev, [key]: value }));
  };
  const canCreate = form.name.trim().length >= 3 && form.facility_type && form.ownership && form.state && form.lga;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    setError('');
    try {
      const record = await facilitiesApi.create({ ...form, name: form.name.trim(), data_status: 'draft' });
      logRegistryProposal('facility', { name: form.name, state: form.state, lga: form.lga }, user);
      onCreated?.(record);
      onClose();
    } catch (err) {
      setError(parseError(err, 'Failed to propose facility.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Propose New Facility</h3>
            <p className="mt-0.5 text-xs text-gray-400">Registry proposal — adds a Draft record to the Facility Core Registry</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-full text-xl leading-none text-gray-400 hover:bg-gray-100">×</button>
        </div>
        <div className="space-y-4 p-6">
          {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-700">{error}</div>}
          <Field label="Facility Name" required hint="Minimum 3 characters">
            <input value={form.name} onChange={set('name')} placeholder="Enter facility name" className={INP} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Facility Type" required>
              <Select value={form.facility_type} onChange={set('facility_type')} options={typeOptions} placeholder="Select facility type" />
            </Field>
            <Field label="Ownership" required>
              <Select value={form.ownership} onChange={set('ownership')} options={OWNERSHIP_OPTIONS} placeholder="Select ownership" />
            </Field>
            <Field label="Functional Status" required>
              <Select value={form.functional_status} onChange={set('functional_status')} options={FUNCTIONAL_STATUS_OPTIONS} placeholder="Select status" />
            </Field>
            <Field label="State" required>
              {stateLocked
                ? <input value={form.state} readOnly className={INP_LOCKED} />
                : <Select value={form.state} onChange={set('state')} options={stateOptions} placeholder="Select state" />}
            </Field>
            <Field label="LGA" required>
              {lgaLocked
                ? <input value={form.lga} readOnly className={INP_LOCKED} />
                : <Select value={form.lga} onChange={set('lga')} options={lgaOptions} disabled={!form.state || loadingLgas}
                    placeholder={form.state ? (loadingLgas ? 'Loading LGAs…' : 'Select LGA') : 'Select state first'} />}
            </Field>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="h-11 flex-1 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Close</button>
            <button type="button" onClick={submit} disabled={!canCreate || busy}
              className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40">
              {busy ? 'Proposing…' : 'Propose'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
